"use client";

import { useUser } from "@clerk/nextjs";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@repo/ui/components/ui/avatar";
import UserFilled from "@/assets/icons/user-filled.svg";
import { BLOCK_CAPTURE_CLASS } from "@/features/analytics/lib/masking";
import { cn } from "@/lib/utils";

const getInitials = (firstName?: string | null, lastName?: string | null) =>
  [firstName, lastName]
    .map((part) => part?.trim()?.[0] ?? "")
    .join("")
    .toUpperCase();

/**
 * Account avatar in its three designed states: the uploaded photo, initials
 * when the profile has a name, and the user icon when it has neither. Clerk
 * always returns an `imageUrl` — a generated one when nothing was uploaded —
 * so the photo is keyed off `hasImage` instead.
 *
 * The header trigger inverts the colours: a white circle on the dark bar,
 * while the menus put a white mark on the dark circle.
 */
export const UserAvatar = ({
  className,
  variant = "dark",
  compact = false,
}: {
  className?: string;
  variant?: "dark" | "light";
  compact?: boolean;
}) => {
  const { user } = useUser();
  const initials = getInitials(user?.firstName, user?.lastName);
  const isLight = variant === "light";

  return (
    <Avatar className={cn(BLOCK_CAPTURE_CLASS, className)}>
      {user?.hasImage ? <AvatarImage src={user.imageUrl} /> : null}
      <AvatarFallback
        className={cn(
          "overflow-hidden font-medium",
          isLight
            ? "bg-background-fill-primary text-brand-green1-300"
            : "bg-brand-green1-300 text-text-inverse"
        )}
      >
        {initials ? (
          // Each size has its own type scale in the design: 11/16 in the
          // header trigger, 16/20 in the menus.
          <span
            className={cn(
              "uppercase",
              compact
                ? "text-[11px] leading-4 tracking-[0.55px]"
                : "text-base leading-5 tracking-[0.8px]"
            )}
          >
            {initials}
          </span>
        ) : (
          // The mark is as wide as the circle and sits low, so the crop leaves
          // the head and just a sliver of the shoulders — as in the design.
          <UserFilled className="size-full translate-y-[16.7%]" />
        )}
      </AvatarFallback>
    </Avatar>
  );
};
